'use client'

import Link from 'next/link'
import { useState, useEffect } from 'react'
import { useLocale } from '~/contexts/LocaleContext'
import ArrowDown from './arrow-down'
import Logo from './logo'
import LanguageSelector from './language-selector'
import AuthSection from './auth-section'
import MenuIcon from '../../../icons/menu-icon'
import CloseIcon from '../../../icons/close-icon'
import CheckIcon from '~/icons/check-icon'
import { getTranslation } from '~/lib/get-translation'
import { useLocalizedNavigation } from '~/hooks/use-localized-navigation'
import type { LanguageCode } from '~/data/language'
import { useMenuItems } from '~/hooks/use-menu-items'
import { buildMenuTree } from '~/utils/menu-tree'

interface MobileHeaderProps {
  isActive: boolean
  isMainMenuOpen: boolean
  toggleMainMenu: () => void
  pathname: string
  isLangMenuOpen: boolean
  setIsLangMenuOpen: (isOpen: boolean) => void
  isScrolled: boolean
  langMenuRef: React.RefObject<HTMLDivElement>
}

export default function MobileHeader({
  isActive,
  isMainMenuOpen,
  toggleMainMenu,
  pathname,
  isLangMenuOpen,
  setIsLangMenuOpen,
  langMenuRef,
}: MobileHeaderProps) {
  const { locale } = useLocale()
  const { getFullUrl } = useLocalizedNavigation()
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({})
  const { data: menuItems = [] } = useMenuItems(locale)
  const menuTree = buildMenuTree(menuItems)
  const listActive = menuTree.filter((r) => r.showMenu)

  useEffect(() => {
    setOpenDropdowns({})
  }, [pathname])

  useEffect(() => {
    if (isMainMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMainMenuOpen])

  const toggleDropdown = (href: string) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [href]: !prev[href],
    }))
  }

  const isDropdownOpen = (href: string) => openDropdowns[href] || false

  const isPathActive = (href: string) => {
    const normalize = (str: string) => str.replace(/\/+$/, '').replace(/^\/+/, '')
    const normalizedPathname = normalize(pathname)
    const normalizedHref = normalize(href)

    // Check exact match
    if (normalizedPathname === normalizedHref) return true

    // Check if current path is a child of the href
    if (normalizedPathname.startsWith(normalizedHref + '/')) return true

    // Check if href is in ALWAYS_ACTIVE_BASE_PATHS
    const ALWAYS_ACTIVE_BASE_PATHS = [
      '/rooms/',
      '/news/',
      '/discount/',
      '/contact',
      '/policy-payment',
      '/policy',
      '/regular',
    ]

    // Check if current path matches any always active paths
    const isAlwaysActive = ALWAYS_ACTIVE_BASE_PATHS.some((path) => {
      const fullPath = `/${locale}${path}`
      // Exact match
      if (normalizedPathname === normalize(fullPath)) return true
      // Child path match for paths ending with /
      if (path.endsWith('/') && normalizedPathname.startsWith(normalize(fullPath))) return true
      // Child path match for paths without trailing /
      if (!path.endsWith('/') && normalizedPathname.startsWith(normalize(fullPath) + '/'))
        return true
      return false
    })

    if (isAlwaysActive) return true

    // Special case for hotels path
    if (normalizedPathname.startsWith(`/${locale}/hotels`)) {
      // For mobile or when path includes /service/
      if (normalizedPathname.includes('/service/')) {
        return true
      }
    }

    return false
  }

  const handleLinkClick = () => {
    if (isMainMenuOpen) {
      toggleMainMenu()
    }
  }

  return (
    <div className="w-full md:hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Logo isScrolled={isActive} />
        <div className="flex items-center space-x-2">
          <LanguageSelector
            isActive={isActive}
            isLangMenuOpen={isLangMenuOpen}
            setIsLangMenuOpen={setIsLangMenuOpen}
            langMenuRef={langMenuRef}
          />
          <div className="md:hidden">
            <AuthSection isActive={isActive} />
          </div>
          <div className="h-6 w-fit">
            <button
              onClick={toggleMainMenu}
              className="transition-colors focus:outline-none"
              aria-label="Toggle mobile menu"
            >
              {isMainMenuOpen ? (
                <CloseIcon className={isActive ? 'text-gray-900' : 'text-white'} />
              ) : (
                <MenuIcon
                  variant={`${isActive ? 'black' : 'white'}`}
                  className={isActive ? 'text-gray-900' : 'text-white'}
                />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${
          isMainMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={toggleMainMenu}
      />

      {/* Menu Container */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full transform bg-white transition-transform duration-300 ease-in-out ${
          isMainMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Menu Header */}
        <div className="flex h-12 items-center justify-between border-b border-[#F3F3F3] px-4">
          <Logo isScrolled={true} />
          <button onClick={toggleMainMenu} className="focus:outline-none">
            <CloseIcon className="text-gray-900" />
          </button>
        </div>

        {/* Menu Content */}
        <div className="h-[calc(100vh-130px)] overflow-y-auto px-4 py-2">
          {listActive.map((link, index) => (
            <div key={link.href}>
              {index > 0 && <div className="mx-0 my-0 border-t border-[#F3F3F3]"></div>}
              {link.children ? (
                <div className="space-y-0">
                  <button
                    onClick={() => toggleDropdown(link.href)}
                    className={`font-body relative flex w-full items-center justify-between rounded px-1 py-2 font-lora text-base font-medium leading-6 ${
                      isPathActive(link.href)
                        ? 'bg-primary-50 font-bold text-primary-500'
                        : 'text-gray-900 hover:bg-primary-50 hover:font-bold hover:text-primary-500'
                    }`}
                  >
                    {link.titleKey}
                    <div
                      className={`transform transition-transform duration-200 ${
                        isDropdownOpen(link.href) ? 'rotate-180' : ''
                      }`}
                    >
                      <ArrowDown
                        variant={isPathActive(link.href) ? 'primary' : 'black'}
                        className="text-gray-900"
                      />
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isDropdownOpen(link.href) ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        href={getFullUrl(child.href)}
                        onClick={handleLinkClick}
                        className={`font-body relative flex items-center justify-between rounded-[2px] px-1 py-2 pl-3 font-lexend text-[14px] font-light leading-6 ${
                          isPathActive(child.href)
                            ? 'bg-primary-50 font-bold text-primary-500'
                            : 'text-gray-900 hover:bg-primary-50 hover:font-bold hover:text-primary-500'
                        }`}
                      >
                        {child.titleKey}
                        {isPathActive(child.href) && (
                          <CheckIcon className="mr-2 text-primary-500" />
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  href={getFullUrl(link.href)}
                  onClick={handleLinkClick}
                  className={`font-body relative block rounded px-1 py-2 font-lora text-base font-medium leading-7 ${
                    isPathActive(link.href)
                      ? 'bg-primary-50 font-bold text-primary-500'
                      : 'text-[#2D2E32] hover:bg-primary-50 hover:font-bold hover:text-primary-500'
                  }`}
                >
                  {link.titleKey}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
